/**
 * MobileBrokerFees Component
 *
 * Mobile page for managing broker fee configurations (fee tree list).
 * Route: /settings/portfolio/reference-data/brokers/:brokerId/fees
 *
 * Selecting a node navigates to the editor route (MobileFeeEditorPage).
 * Add/copy success handlers also navigate to the editor route.
 *
 * Features: URL param validation, broker context sync, fee tree with search,
 * add/copy popovers and modals, kebab actions, route-based editor navigation.
 */

// 1. External dependencies
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Plus } from 'lucide-react';

// 2. Workspace packages
import {
  Card,
  CardHeader,
  CardBody,
  Breadcrumbs,
  BreadcrumbItem,
  Input,
  ScrollShadow,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Relative — domain context, components, and utilities
import { useBrokers } from '../Brokers/context';
import { FeeTree } from '../Brokers/feeTree';
import type { FeeTreeNode, FeeTreeActionType } from '../Brokers/feeTree';
import { AddCategoryModal } from '../Brokers/AddCategoryModal';
import { AddInstrumentModal } from '../Brokers/AddInstrumentModal';
import { CopyFeeModal } from '../Brokers/CopyFeeModal';

// =============================================================================
// Route Constants
// =============================================================================

const SETTINGS_ROUTE = '/settings';
const PORTFOLIO_ROUTE = '/settings/portfolio';
const BROKER_LIST_ROUTE = '/settings/portfolio/reference-data/brokers';

// =============================================================================
// Component
// =============================================================================

export const MobileBrokerFees: React.FC = () => {
  const { brokerId } = useParams<{ brokerId: string }>();
  const navigate = useNavigate();

  // Context
  const {
    brokers,
    selectedBroker,
    selectedBrokerId,
    loading,
    showArchived,
    setShowArchived,
    selectBroker,
    removeFeeConfiguration,
  } = useBrokers();

  // ===========================================================================
  // Fee Tree State
  // ===========================================================================

  const [feeSearchQuery, setFeeSearchQuery] = useState('');
  const [isAddFeePopoverOpen, setIsAddFeePopoverOpen] = useState(false);
  const [addCategoryState, setAddCategoryState] = useState<{ parentCategoryPath: string[] } | null>(null);
  const [addInstrumentState, setAddInstrumentState] = useState<{ parentCategoryPath: string[] } | null>(null);

  // Remove confirmation state
  const [removingNode, setRemovingNode] = useState<FeeTreeNode | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Copy modal state
  const [copyState, setCopyState] = useState<{ sourceNode: FeeTreeNode } | null>(null);
  const lastCopySourceRef = useRef<FeeTreeNode | null>(null);
  if (copyState) lastCopySourceRef.current = copyState.sourceNode;

  // ===========================================================================
  // URL Param Validation & Context Sync
  // ===========================================================================

  useEffect(() => {
    // Wait for brokers to load before any validation
    if (loading) {
      return;
    }

    if (!brokerId) {
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    // Validate against full brokers array (not filtered)
    const broker = brokers.find((b) => b.id === brokerId);

    if (!broker) {
      // Broker not found — redirect to list
      navigate(BROKER_LIST_ROUTE, { replace: true });
      return;
    }

    // For archived deep-links: toggle showArchived first, then return
    // from this effect tick to let state settle before selecting
    if (broker.status === 'archived' && !showArchived) {
      setShowArchived(true);
      return;
    }

    // Sync to context if not already selected
    if (selectedBrokerId !== brokerId) {
      selectBroker(brokerId);
    }
  }, [brokerId, brokers, loading, selectedBrokerId, showArchived, setShowArchived, selectBroker, navigate]);

  // ===========================================================================
  // Broker-Change Reset
  // ===========================================================================

  // Reset fee tree UI state when brokerId changes — prevents stale state
  // when navigating between brokers (taxonomy-based node IDs can collide)
  useEffect(() => {
    setFeeSearchQuery('');
    setIsAddFeePopoverOpen(false);
    setAddCategoryState(null);
    setAddInstrumentState(null);
    setRemovingNode(null);
    setIsRemoving(false);
    setCopyState(null);
  }, [brokerId]);

  // ===========================================================================
  // Toast
  // ===========================================================================

  const { showToast } = useToastNotification();

  // ===========================================================================
  // Handlers
  // ===========================================================================

  // Node selection — navigate to editor route
  const handleFeeNodeSelect = useCallback((nodeId: string | null): void => {
    if (nodeId !== null && brokerId) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees/${encodeURIComponent(nodeId)}`);
    }
  }, [brokerId, navigate]);

  // Header add popover handlers (root-level add)
  const handleAddFeeCategory = useCallback((): void => {
    setIsAddFeePopoverOpen(false);
    setAddCategoryState({ parentCategoryPath: [] });
  }, []);

  const handleAddFeeInstrument = useCallback((): void => {
    setIsAddFeePopoverOpen(false);
    setAddInstrumentState({ parentCategoryPath: [] });
  }, []);

  // Fee tree row action handler (kebab menu — contextual add)
  const handleFeeAction = useCallback(
    (action: FeeTreeActionType, node: FeeTreeNode): void => {
      switch (action) {
        case 'add-child':
          setAddCategoryState({ parentCategoryPath: node.categoryPath });
          break;
        case 'add-instrument':
          setAddInstrumentState({ parentCategoryPath: node.categoryPath });
          break;
        case 'copy':
          setCopyState({ sourceNode: node });
          break;
        case 'remove':
          setRemovingNode(node);
          break;
        default: {
          const _exhaustive: never = action;
          return _exhaustive;
        }
      }
    },
    []
  );

  // Remove confirmation handlers
  const handleRemoveCancel = useCallback((): void => {
    if (isRemoving) return;
    setRemovingNode(null);
  }, [isRemoving]);

  const handleRemoveConfirm = useCallback(async (): Promise<void> => {
    if (!removingNode || removingNode.feeConfigIds.length === 0) return;

    setIsRemoving(true);
    try {
      let anySucceeded = false;
      for (const configId of removingNode.feeConfigIds) {
        const success = await removeFeeConfiguration(configId);
        if (success) anySucceeded = true;
      }

      if (anySucceeded) {
        showToast({
          title: 'Fee configuration removed',
          description: `"${removingNode.label}" has been archived.`,
          color: 'success',
        });
      } else {
        showToast({
          title: 'Failed to remove fee configuration',
          description: 'The configuration may have already been removed.',
          color: 'danger',
        });
      }
    } catch (error) {
      console.error('Failed to remove fee configuration:', error);
      showToast({
        title: 'Failed to remove fee configuration',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsRemoving(false);
      setRemovingNode(null);
    }
  }, [removingNode, removeFeeConfiguration, showToast]);

  // Add/Copy success handlers — navigate to editor route for new node
  const handleCategoryAdded = useCallback((nodeId: string): void => {
    if (brokerId) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees/${encodeURIComponent(nodeId)}`);
    }
  }, [brokerId, navigate]);

  const handleInstrumentAdded = useCallback((nodeId: string): void => {
    if (brokerId) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees/${encodeURIComponent(nodeId)}`);
    }
  }, [brokerId, navigate]);

  const handleCopyComplete = useCallback((nodeId: string): void => {
    if (brokerId) {
      navigate(`${BROKER_LIST_ROUTE}/${brokerId}/fees/${encodeURIComponent(nodeId)}`);
    }
  }, [brokerId, navigate]);

  // ===========================================================================
  // Loading / Not-Found Guard
  // ===========================================================================

  // Guard: prevent rendering stale broker during URL-param transitions
  if (!selectedBroker || selectedBroker.id !== brokerId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <>
      <Card radius="none" shadow="none" className="h-full flex flex-col">
        {/* Header - Breadcrumb Navigation */}
        <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
          <div className="flex items-center">
            <Breadcrumbs variant="light" size="sm">
              <BreadcrumbItem
                onPress={() => navigate(SETTINGS_ROUTE)}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Settings
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={() => navigate(PORTFOLIO_ROUTE)}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Portfolio
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={() => navigate(BROKER_LIST_ROUTE)}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                Brokers
              </BreadcrumbItem>
              <BreadcrumbItem
                onPress={() => navigate(`${BROKER_LIST_ROUTE}/${brokerId}`)}
                className="text-text-secondary hover:text-text-primary"
                isCurrent={false}
              >
                {selectedBroker.name}
              </BreadcrumbItem>
              <BreadcrumbItem isCurrent={true}>
                Fee Configurations
              </BreadcrumbItem>
            </Breadcrumbs>
          </div>
        </CardHeader>

        {/* Content — Fee Tree with Search and Add */}
        <CardBody className="flex-1 flex flex-col min-h-0 px-1 pb-2">
          {/* Search / Add Header */}
          <div className="shrink-0 flex gap-2 items-center py-1">
            <Input
              placeholder="Search..."
              size="sm"
              value={feeSearchQuery}
              onValueChange={setFeeSearchQuery}
              startContent={<Search className="w-4 h-4 text-text-secondary" />}
              isClearable
              className="flex-1"
              classNames={{ input: 'text-base' }}
            />
            <Popover
              isOpen={isAddFeePopoverOpen}
              onOpenChange={setIsAddFeePopoverOpen}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  aria-label="Add fee configuration"
                >
                  <Plus size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-1">
                <div className="flex flex-col min-w-40">
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    onPress={handleAddFeeCategory}
                  >
                    Add Fee Category
                  </Button>
                  <Button
                    size="md"
                    variant="light"
                    className="justify-start"
                    onPress={handleAddFeeInstrument}
                  >
                    Add Instrument
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Fee Tree */}
          <ScrollShadow hideScrollBar className="flex-1 min-h-0">
            <FeeTree
              brokerId={selectedBroker.id}
              feeConfigurations={selectedBroker.feeConfigurations}
              selectedNodeId={null}
              onSelectNode={handleFeeNodeSelect}
              onAction={handleFeeAction}
              searchQuery={feeSearchQuery}
            />
          </ScrollShadow>
        </CardBody>
      </Card>

      {/* Add Category Modal */}
      <AddCategoryModal
        isOpen={!!addCategoryState}
        onClose={() => setAddCategoryState(null)}
        parentCategoryPath={addCategoryState?.parentCategoryPath ?? []}
        onAdded={handleCategoryAdded}
      />

      {/* Add Instrument Modal */}
      <AddInstrumentModal
        isOpen={!!addInstrumentState}
        onClose={() => setAddInstrumentState(null)}
        parentCategoryPath={addInstrumentState?.parentCategoryPath ?? []}
        onAdded={handleInstrumentAdded}
      />

      {/* Remove Fee Configuration Confirmation Modal */}
      <Modal
        isOpen={!!removingNode}
        onOpenChange={(open) => !open && handleRemoveCancel()}
        isDismissable={!isRemoving}
        hideCloseButton={isRemoving}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>Remove Fee Configuration</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              Are you sure you want to remove the fee configuration for &quot;{removingNode?.label}&quot;?
            </p>
            <p className="text-sm text-text-tertiary">
              The configuration will be archived and can be restored by adding it again.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={handleRemoveCancel} isDisabled={isRemoving}>
              Cancel
            </Button>
            <Button color="warning" onPress={handleRemoveConfirm} isLoading={isRemoving}>
              Remove
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Copy Fee Modal — ref pattern preserves close animation */}
      {lastCopySourceRef.current && (
        <CopyFeeModal
          isOpen={!!copyState}
          onClose={() => setCopyState(null)}
          sourceNode={lastCopySourceRef.current}
          feeConfigurations={selectedBroker.feeConfigurations}
          onCopied={handleCopyComplete}
        />
      )}
    </>
  );
};
